import { useState } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { userDetailSelect } from "Layout/slice/selector";
import { useCreateCashTransfer } from "hooks/useCreateCashTransfer";
import { useMutation } from "hooks/useMutation";
import { Loader } from "components/Loader";
import { ChervonRightIcon } from "assets/icons";
import { TransferFundsModalContainer } from "./TransferFundsModal.styles";
import { TransactionModal } from "../TransactionModal";
import { TransactionConfirmationModal } from "../TransactionConfirmationModal";
import { TransactionSuccessModal } from "../TransactionSuccessModal";
import { createCashTransfer } from "features/SurmountAccount/service";
import { toast } from "Layout/slice";
import { fetchUserDetails } from "Layout/slice/thunk";
import { toast as sonnerToast } from "sonner";
import { parseErrorMessage } from "utils";
import { STATUS_CODES } from "utils/constants";

const TransferFundsModal = ({
  show,
  onHide,
  onDepositClick,
  onWithdrawClick,
}) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(userDetailSelect);
  // Money leaving the platform needs a fresh second factor when the user has
  // opted in, even inside an authenticated session — but only while the
  // organization allows MFA at all, the same gate the Security settings use.
  const withdrawalMfaEnabled =
    Boolean(userDetails?.mfa_allowed) &&
    Boolean(userDetails?.withdrawal_mfa_enabled);
  const { accountDetails, isLoadingAccountDetails, refetchAccountDetails } =
    useCreateCashTransfer();
  const queryClient = useQueryClient();
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDepositConfirmationModal, setShowDepositConfirmationModal] =
    useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showWithdrawConfirmationModal, setShowWithdrawConfirmationModal] =
    useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState({
    type: "deposit",
    amount: 0,
  });
  const [depositData, setDepositData] = useState(null);
  const [withdrawData, setWithdrawData] = useState(null);
  // Set when the server answered 428: verification is required for this
  // withdrawal whatever the profile in Redux said.
  const [stepUpRequiredByServer, setStepUpRequiredByServer] = useState(false);

  // After a deposit or withdrawal settles, refresh the cashTransfers cache
  // behind the dashboard's Recent Activity so the transfer the user just made
  // shows up without a manual reload. The partial key matches regardless of
  // the pageSize suffix on the infinite query.
  const refreshRecentActivity = () => {
    queryClient.invalidateQueries({ queryKey: ["cashTransfers"] });
  };

  // Deposit Mutation
  const depositMutation = useMutation({
    mutationFn: (payload) => createCashTransfer(payload),
    onSuccess: () => {
      // Close confirmation modal and show success modal
      setShowDepositConfirmationModal(false);
      setShowDepositModal(false);
      setSuccessData({ type: "deposit", amount: depositData.amount });
      setShowSuccessModal(true);
      refetchAccountDetails();
      refreshRecentActivity();
    },
    showErrorToast: false,
    onError: (error) => {
      dispatch(
        toast({
          type: "error",
          body:
            parseErrorMessage(error) ||
            "Failed to process deposit. Please try again.",
        })
      );
    },
  });

  // Withdraw Mutation
  const withdrawMutation = useMutation({
    mutationFn: (payload) => createCashTransfer(payload),
    onSuccess: () => {
      // Close confirmation modal and show success modal
      setShowWithdrawConfirmationModal(false);
      setShowWithdrawModal(false);
      setStepUpRequiredByServer(false);
      setSuccessData({ type: "withdraw", amount: withdrawData.amount });
      setShowSuccessModal(true);
      refetchAccountDetails();
      refreshRecentActivity();
    },
    showErrorToast: false,
    onError: (error) => {
      // 428 means the server wants a code we did not send — our copy of the
      // user's settings was stale, because the switch was turned on in another
      // tab or on another device. Recover in place: pull a fresh profile and
      // send the user to code entry, instead of leaving them with a toast and
      // no way to satisfy the demand short of reloading the page.
      if (
        error?.response?.status === STATUS_CODES.PRECONDITION_REQUIRED &&
        !stepUpRequiredByServer
      ) {
        setStepUpRequiredByServer(true);
        dispatch(fetchUserDetails());
        return;
      }
      // Surfaces the step-up `detail` ("Additional verification is required.")
      // when withdrawal MFA is enabled, plus any other withdrawal failure.
      sonnerToast.error(
        parseErrorMessage(error) ||
          "Failed to process withdrawal. Please try again."
      );
    },
  });

  const handleDepositClick = () => {
    if (onDepositClick) {
      onDepositClick(accountDetails);
    } else {
      // Open the deposit modal
      setShowDepositModal(true);
    }
  };

  const handleDepositModalHide = () => {
    setShowDepositModal(false);
  };

  const handleDepositNext = (data) => {
    setDepositData(data);
    setShowDepositModal(false);
    setShowDepositConfirmationModal(true);
  };

  const handleDepositConfirmationHide = () => {
    setShowDepositConfirmationModal(false);
    // Optionally go back to deposit modal
    // setShowDepositModal(true);
  };

  const handleDepositConfirm = () => {
    const payload = {
      origin_account: "plaid_bank",
      destination_account: "alpaca",
      amount: parseFloat(depositData.amount),
    };
    depositMutation.mutate(payload);
  };

  const handleWithdrawClick = () => {
    if (onWithdrawClick) {
      onWithdrawClick(accountDetails);
    } else {
      // Open the withdraw modal
      setShowWithdrawModal(true);
    }
  };

  const handleWithdrawModalHide = () => {
    setShowWithdrawModal(false);
  };

  const handleWithdrawNext = (data) => {
    setWithdrawData(data);
    setShowWithdrawModal(false);
    setShowWithdrawConfirmationModal(true);
  };

  const handleWithdrawConfirmationHide = () => {
    setShowWithdrawConfirmationModal(false);
    setStepUpRequiredByServer(false);
    // Optionally go back to withdraw modal
    // setShowWithdrawModal(true);
  };

  const handleWithdrawConfirm = (stepUpToken) => {
    const payload = {
      origin_account: "alpaca",
      destination_account: "plaid_bank",
      amount: parseFloat(withdrawData.amount),
      // Present only when withdrawal MFA is enabled; the modal verifies the
      // SMS code and hands back the single-use token.
      ...(stepUpToken ? { step_up_token: stepUpToken } : {}),
    };
    withdrawMutation.mutate(payload);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Close the main transfer modal
    onHide();
  };

  return (
    <>
      <TransferFundsModalContainer
        show={
          show &&
          !showDepositModal &&
          !showDepositConfirmationModal &&
          !showWithdrawModal &&
          !showWithdrawConfirmationModal &&
          !showSuccessModal
        }
        onHide={onHide}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            Transfer funds to or from Surmount account.
          </h2>
        </div>

        <div className="modal-body-content">
          {isLoadingAccountDetails ? (
            <div className="loader-wrapper">
              <Loader />
            </div>
          ) : (
            <>
              {/* Deposit Section */}
              <div className="transfer-section" onClick={handleDepositClick}>
                <div className="transfer-section-left">
                  <div className="transfer-content">
                    <h3 className="transfer-title">Deposit</h3>
                    <p className="transfer-description">
                      Deposit funds from your bank to your Surmount account.
                    </p>
                  </div>
                </div>
                <div className="transfer-section-right">
                  <ChervonRightIcon />
                </div>
              </div>
              <div className="divider" />
              {/* Withdraw Section */}
              <div className="transfer-section" onClick={handleWithdrawClick}>
                <div className="transfer-section-left">
                  <div className="transfer-content">
                    <h3 className="transfer-title">Withdraw</h3>
                    <p className="transfer-description">
                      Transfer money from your Surmount account to your bank.
                    </p>
                  </div>
                </div>
                <div className="transfer-section-right">
                  <ChervonRightIcon />
                </div>
              </div>
            </>
          )}
        </div>
      </TransferFundsModalContainer>

      <TransactionModal
        show={showDepositModal}
        onHide={handleDepositModalHide}
        onNext={handleDepositNext}
        accountDetails={accountDetails}
        type="deposit"
      />

      <TransactionConfirmationModal
        show={showDepositConfirmationModal}
        onHide={handleDepositConfirmationHide}
        onConfirm={handleDepositConfirm}
        transactionData={depositData}
        isLoading={depositMutation.isLoading}
        type="deposit"
      />

      <TransactionModal
        show={showWithdrawModal}
        onHide={handleWithdrawModalHide}
        onNext={handleWithdrawNext}
        accountDetails={accountDetails}
        type="withdraw"
      />

      <TransactionConfirmationModal
        show={showWithdrawConfirmationModal}
        onHide={handleWithdrawConfirmationHide}
        onConfirm={handleWithdrawConfirm}
        transactionData={withdrawData}
        isLoading={withdrawMutation.isLoading}
        type="withdraw"
        requireVerification={withdrawalMfaEnabled || stepUpRequiredByServer}
        autoStartVerification={stepUpRequiredByServer}
      />

      <TransactionSuccessModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        type={successData.type}
        amount={successData.amount}
      />
    </>
  );
};

TransferFundsModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onDepositClick: PropTypes.func,
  onWithdrawClick: PropTypes.func,
};

export { TransferFundsModal };
